import axios from 'axios';

/**
 * API 响应格式
 */
interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

/**
 * 页面配置响应（新格式）
 */
export interface PageConfigResponse {
  page: {
    serviceName: string;
    pageId: string;
  };
  components: ComponentConfig[];
}

/**
 * 组件配置（新格式）
 */
export interface ComponentConfig {
  id: string;
  componentId: string;
  name: string;
  type: string;
  displayInfo?: string;
  description?: string;
  parentId?: string;
  children: ComponentConfig[];
  datasets: ComponentDataset[];
  thinkingModes: ThinkingModeConfig[];
}

/**
 * 组件数据集
 */
export interface ComponentDataset {
  datasetId: string;
  dataKey?: string;
  isPrimary: boolean;
  useRenderedData: boolean;
  paramOverrides?: Record<string, any>;
  dataset: {
    datasetId: string;
    name: string;
    description?: string;
    methodType: string;
    methodName: string;
    serviceName?: string;
    paramTemplate?: Record<string, any>;
    defaultParams?: Record<string, any>;
    dataFormat?: string;
    dataPath?: string;
    fields: DatasetField[];
  };
}

/**
 * 数据集字段
 */
export interface DatasetField {
  fieldId: string;
  fieldPath?: string;
  fieldName: string;
  fieldType: string;
  dataLevel: number;
  isNested: boolean;
  description?: string;
  isSensitive?: boolean;
  maskRule?: string;
  maskPlaceholder?: string;
  maskPattern?: Record<string, any>;
  isEnum?: boolean;
  enumMapping?: Record<string, string>;
}

/**
 * 思考方式配置（新格式）
 */
export interface ThinkingModeConfig {
  thinkingModeId: string;
  label: string;
  description?: string;
  isDefault: boolean;
  sortOrder: number;
  promptTemplate?: string;
  includeFields?: string[];
  excludeFields?: string[];
  maskFields?: Record<string, string>;
  responseFormat?: ResponseFormat;
}

/**
 * 返回格式
 */
export interface ResponseFormat {
  formatType: string;
  schema?: string;
  template?: string;
  requirements?: string;
  constraints?: Record<string, any>;
  maxLength?: number;
  positiveWords?: string[];
  negativeWords?: string[];
}

/**
 * AI 分析请求参数（新格式）
 */
export interface AiAnalyzeRequest {
  componentId: string;
  thinkingModeId: string;
  data: {
    primary: any;
    comparison?: any[];
  };
  fieldDefinitions?: any[];
}

/**
 * AI 分析响应
 */
export interface AiAnalyzeResponse {
  result: string;
  thinkingModeId: string;
  resultFormat: string;
}

/**
 * AI 配置 API 服务类
 */
class AiConfigApi {
  /**
   * 获取 msAi 服务地址
   * 开发环境使用代理路径，生产环境使用完整 URL
   */
  private getMsAiBaseUrl(): string {
    const envUrl = process.env.REACT_APP_MSAI_URL || process.env.REACT_APP_MSAI_BASE_URL;
    const rawUrl = envUrl?.trim();
    const isHttpsPage =
      typeof window !== 'undefined' && typeof window.location?.protocol === 'string'
        ? window.location.protocol === 'https:'
        : false;
    // 在 HTTPS 页面上，若配置了 http:// 的 msAi 地址，会触发 Mixed Content 直接被浏览器拦截。
    // 这里做一个安全升级：仅当页面是 HTTPS 且 url 明确为 http:// 时替换为 https://
    if (rawUrl) {
      return isHttpsPage && rawUrl.startsWith('http://')
        ? `https://${rawUrl.slice('http://'.length)}`
        : rawUrl;
    }
    
    // 开发环境：使用代理路径（通过 setupProxy.js 代理到 localhost:50083）
    if (process.env.NODE_ENV === 'development') {
      return ''; // 空字符串表示使用相对路径，走代理
    }
    
    // 生产环境：使用完整 URL
    return isHttpsPage ? 'https://ai.homaxs.com' : 'http://ai.homaxs.com';
  }

  /**
   * 获取页面配置（新格式）
   * @param serviceName - 微服务名称，如 'msCentral'
   * @param pageId - 页面标识，如 'income'
   * @returns Promise<PageConfigResponse>
   */
  async getPageConfig(serviceName: string, pageId: string): Promise<PageConfigResponse> {
    try {
      const msAiBaseUrl = this.getMsAiBaseUrl();
      const response = await axios.get<ApiResponse<PageConfigResponse>>(
        `${msAiBaseUrl}/api/ai/config/page`,
        {
          params: {
            service_name: serviceName,
            page_id: pageId,
          },
        }
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取配置失败');
      }
    } catch (error: any) {
      console.error('获取页面配置失败:', error);
      throw new Error(error.response?.data?.message || error.message || '获取配置失败');
    }
  }

  /**
   * 获取组件配置
   * @param componentId - 组件标识
   * @returns Promise<ComponentConfig>
   */
  async getComponentConfig(componentId: string): Promise<ComponentConfig> {
    try {
      const msAiBaseUrl = this.getMsAiBaseUrl();
      const response = await axios.get<ApiResponse<ComponentConfig>>(
        `${msAiBaseUrl}/api/ai/config/component/${componentId}`
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取配置失败');
      }
    } catch (error: any) {
      console.error('获取组件配置失败:', error);
      throw new Error(error.response?.data?.message || error.message || '获取配置失败');
    }
  }

  /**
   * 获取数据集列表
   * @returns Promise<any[]>
   */
  async getDatasetList(): Promise<any[]> {
    try {
      const msAiBaseUrl = this.getMsAiBaseUrl();
      // 注意：需要后端提供列表接口
      // 临时方案：如果接口不存在，返回空数组
      try {
        const response = await axios.get<ApiResponse<any[]>>(
          `${msAiBaseUrl}/api/ai/config/datasets`
        );
        if (response.data.code === 200) {
          return response.data.data || [];
        }
      } catch (error: any) {
        // 如果接口不存在（404），返回空数组
        if (error.response?.status === 404) {
          console.warn('数据集列表接口未实现，返回空数组');
          return [];
        }
        throw error;
      }
      return [];
    } catch (error: any) {
      console.error('获取数据集列表失败:', error);
      // 如果接口不存在，返回空数组而不是抛出错误
      if (error.response?.status === 404) {
        return [];
      }
      throw new Error(error.response?.data?.message || error.message || '获取数据集列表失败');
    }
  }

  /**
   * 获取数据集配置
   * @param datasetId - 数据集标识
   * @returns Promise<any>
   */
  async getDatasetConfig(datasetId: string): Promise<any> {
    try {
      const msAiBaseUrl = this.getMsAiBaseUrl();
      const response = await axios.get<ApiResponse<any>>(
        `${msAiBaseUrl}/api/ai/config/dataset/${datasetId}`
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取配置失败');
      }
    } catch (error: any) {
      console.error('获取数据集配置失败:', error);
      throw new Error(error.response?.data?.message || error.message || '获取配置失败');
    }
  }

  /**
   * 获取思考方式配置
   * @param thinkingModeId - 思考方式标识
   * @returns Promise<any>
   */
  async getThinkingModeConfig(thinkingModeId: string): Promise<any> {
    try {
      const msAiBaseUrl = this.getMsAiBaseUrl();
      const response = await axios.get<ApiResponse<any>>(
        `${msAiBaseUrl}/api/ai/config/thinking-mode/${thinkingModeId}`
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '获取配置失败');
      }
    } catch (error: any) {
      console.error('获取思考方式配置失败:', error);
      throw new Error(error.response?.data?.message || error.message || '获取配置失败');
    }
  }

  /**
   * 创建或更新数据集
   * @param datasetData - 数据集数据
   * @returns Promise<any>
   */
  async upsertDataset(datasetData: any): Promise<any> {
    try {
      const msAiBaseUrl = this.getMsAiBaseUrl();
      const response = await axios.post<ApiResponse<any>>(
        `${msAiBaseUrl}/api/ai/config/dataset`,
        datasetData
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '保存失败');
      }
    } catch (error: any) {
      console.error('保存数据集失败:', error);
      throw new Error(error.response?.data?.message || error.message || '保存失败');
    }
  }

  /**
   * 创建或更新组件
   * @param componentData - 组件数据
   * @returns Promise<any>
   */
  async upsertComponent(componentData: any): Promise<any> {
    try {
      const msAiBaseUrl = this.getMsAiBaseUrl();
      const response = await axios.post<ApiResponse<any>>(
        `${msAiBaseUrl}/api/ai/config/component`,
        componentData
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '保存失败');
      }
    } catch (error: any) {
      console.error('保存组件失败:', error);
      throw new Error(error.response?.data?.message || error.message || '保存失败');
    }
  }

  /**
   * 创建或更新思考方式
   * @param thinkingModeData - 思考方式数据
   * @returns Promise<any>
   */
  async upsertThinkingMode(thinkingModeData: any): Promise<any> {
    try {
      const msAiBaseUrl = this.getMsAiBaseUrl();
      const response = await axios.post<ApiResponse<any>>(
        `${msAiBaseUrl}/api/ai/config/thinking-mode`,
        thinkingModeData
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '保存失败');
      }
    } catch (error: any) {
      console.error('保存思考方式失败:', error);
      throw new Error(error.response?.data?.message || error.message || '保存失败');
    }
  }

  /**
   * 调用 AI 分析（新格式，使用配置系统）
   * @param params - AI 分析请求参数
   * @returns Promise<AiAnalyzeResponse>
   */
  async analyze(params: AiAnalyzeRequest): Promise<AiAnalyzeResponse> {
    try {
      const { componentId, thinkingModeId, data: requestData } = params;
      
      console.log("🔍 [aiConfigApi.analyze] 接收参数:", {
        componentId,
        thinkingModeId,
        hasData: !!requestData,
        paramsKeys: Object.keys(params),
      });
      
      if (!componentId) {
        console.error("❌ [aiConfigApi.analyze] componentId 为空，params:", params);
        throw new Error('componentId 不能为空');
      }
      
      if (!thinkingModeId) {
        console.error("❌ [aiConfigApi.analyze] thinkingModeId 为空，params:", params);
        throw new Error('thinkingModeId 不能为空');
      }
      
      // 1. 获取组件配置
      console.log("📋 [aiConfigApi.analyze] 获取组件配置，componentId:", componentId);
      const componentConfig = await this.getComponentConfig(componentId);
      
      // 2. 找到对应的思考方式
      const thinkingMode = componentConfig.thinkingModes.find(
        tm => tm.thinkingModeId === thinkingModeId
      );
      
      if (!thinkingMode) {
        throw new Error(`思考方式不存在: ${thinkingModeId}`);
      }

      // 3. 构建提示词（使用配置中的 promptTemplate）
      let prompt = thinkingMode.promptTemplate || `请以${thinkingMode.label}的方式处理以下数据：\n\n`;
      
      // 替换模板变量
      prompt = prompt
        .replace(/{description}/g, componentConfig.description || '')
        .replace(/{componentName}/g, componentConfig.name)
        .replace(/{displayInfo}/g, componentConfig.displayInfo || '')
        .replace(/{data}/g, JSON.stringify(requestData.primary, null, 2))
        .replace(/{fields}/g, componentConfig.datasets[0]?.dataset.fields.map(f => f.fieldName).join(', ') || '');

      // 如果有对比数据，添加到提示词
      if (requestData.comparison && requestData.comparison.length > 0) {
        prompt += `\n\n对比数据：\n${JSON.stringify(requestData.comparison, null, 2)}`;
      }

      // 4. 添加返回格式要求
      if (thinkingMode.responseFormat) {
        const rf = thinkingMode.responseFormat;
        if (rf.requirements) {
          prompt += `\n\n结果必须包含以下内容：\n${rf.requirements}`;
        }
        if (rf.maxLength) {
          prompt += `\n\n结果长度请控制在${rf.maxLength}字符以内。`;
        }
      }

      // 5. 调用 msAi 接口
      const msAiBaseUrl = this.getMsAiBaseUrl();
      const response = await axios.post<ApiResponse<{
        content: string;
        model: string;
        provider: string;
        tokensUsed?: number;
        duration?: number;
      }>>(
        `${msAiBaseUrl}/api/ai/call`,
        {
          prompt,
          accessTag: 'internal',
          accessType: 'api',
          serviceName: 'msCentral',
          options: {
            temperature: 0.7,
            maxTokens: thinkingMode.responseFormat?.maxLength || 2000,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 60000,
        }
      );

      if (response.data.code === 1 && response.data.data) {
        return {
          result: response.data.data.content,
          thinkingModeId: thinkingModeId,
          resultFormat: thinkingMode.responseFormat?.formatType || 'text',
        };
      } else {
        throw new Error(response.data.message || 'AI分析失败');
      }
    } catch (error: any) {
      console.error('AI分析失败:', error);
      console.error('错误详情:', {
        componentId: params.componentId || (params as any).component_id,
        thinkingModeId: params.thinkingModeId || (params as any).thinking_mode_id,
        errorMessage: error.message,
        errorResponse: error.response?.data,
      });
      
      let errorMessage = 'AI分析失败';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * 使用自定义提示词调用 AI（不依赖组件配置，用于独立组件页等场景）
   * @param prompt - 完整提示词内容
   * @param meta - 可选的门店续聊信息（msCentral 会按 userId+storeId 维护 10 分钟续聊窗口）
   * @returns Promise<string> 返回 AI 生成的文本
   */
  async analyzeWithPrompt(
    prompt: string,
    meta?: { storeId?: string | number; userId?: string; responseId?: string; reResponseId?: string }
  ): Promise<string> {
    try {
      const token =
        (typeof localStorage !== 'undefined' && localStorage.getItem('token')) ||
        (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('token')) ||
        '';
      const response = await axios.post<ApiResponse<{
        content: string;
        model: string;
        provider: string;
        tokensUsed?: number;
        duration?: number;
      }>>(
        `/api/ai/call`,
        {
          prompt,
          accessTag: 'internal',
          accessType: 'api',
          serviceName: 'msCentral',
          ...(meta?.storeId != null ? { storeId: meta.storeId } : {}),
          ...(meta?.userId ? { userId: meta.userId } : {}),
          ...(meta?.responseId ? { responseId: meta.responseId } : {}),
          ...(meta?.reResponseId ? { reResponseId: meta.reResponseId } : {}),
          options: {
            temperature: 0.7,
            maxTokens: 2000,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
          timeout: 60000,
        }
      );

      if (response.data.code === 1 && response.data.data?.content != null) {
        return response.data.data.content;
      }
      throw new Error(response.data.message || 'AI分析失败');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'AI分析失败';
      throw new Error(message);
    }
  }
}

// 导出单例
export const aiConfigApi = new AiConfigApi();
